import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { isAppUnavailablePayload } from "helpers/apiErrorToast";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TEntity } from "./beneficiaryStore";
import type { TFetchStatus } from "./types";

export type CustomFieldValue = "number" | "string" | "longstring" | "date" | "link" | "fileimport" | "boolean";

export interface ICustomField {
    id: number;
    companyId: number;
    relatedResource: "beneficiary" | "title" | "term";
    uuid: string;
    name: string;
    helperText: string;
    valueType: CustomFieldValue;
    isRequired: number;
    isDeleted: boolean;
    showOnCreation: number;
    showOnPortal: number;
    includeInSalesExport: number;
    formLocation: string;
    entityType: TEntity;
}

export type CustomFieldResource = ICustomField["relatedResource"];

export type CustomFieldWriteData = {
    name: string;
    helperText: string;
    valueType: CustomFieldValue;
    formLocation: string;
    entityType: TEntity;
    isRequired: boolean;
    showOnCreation: boolean;
    showOnPortal: boolean;
    includeInSalesExport: boolean;
    relatedResource?: CustomFieldResource;
    uuid?: string;
};

export const selectCustomFieldsForResource =
    (resource: CustomFieldResource) => (state: { customFields: { customFields: ICustomField[] } }) =>
        state.customFields.customFields.filter((field) => field.relatedResource === resource);

export type StoreCustomField = {
    beneficiaryId: number;
    customFieldTypeId: number;
    value: any;
};

// Lists existing custom fields (all resources mixed):
export const fetchCustomFields = createAsyncThunk("customFields/fetchCustomFields", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/custom-fields");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Creates a new custom field:
export const createCustomField = createAsyncThunk(
    "customFields/createCustomField",
    async ({ data }: { data: CustomFieldWriteData }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/custom-fields", data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Lists custom fields of one resource type (ex: "beneficiary"):
export const fetchCustomFieldsOfResource = createAsyncThunk(
    "customFields/fetchCustomFieldsOfResource",
    async ({ resourceType }: { resourceType: CustomFieldResource }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/custom-fields/resources/${resourceType}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Gets on custom field in particular:
export const fetchCustomField = createAsyncThunk(
    "customFields/fetchCustomField",
    async ({ fieldId }: { fieldId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/custom-fields/${fieldId}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Updates a custom field:
export const updateCustomField = createAsyncThunk(
    "customFields/updateCustomField",
    async (
        { fieldId, data }: { fieldId: number; data: Omit<CustomFieldWriteData, "uuid" | "relatedResource"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/custom-fields/${fieldId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.reponse.data);
        }
    },
);

// Deletes a custom field:
export const deleteCustomField = createAsyncThunk(
    "customFields/deleteCustomField",
    async ({ fieldId }: { fieldId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/custom-fields/${fieldId}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.reponse.data);
        }
    },
);

interface FieldsInitialState {
    customFields: ICustomField[];
    resourceCustomFields: ICustomField[];
    resourceCustomFieldsStatus: TFetchStatus;
    customField: ICustomField | null;
    customFieldsStatus: TFetchStatus;
}

const initialState: FieldsInitialState = {
    customFields: [],
    resourceCustomFields: [],
    resourceCustomFieldsStatus: "idle",
    customField: null,
    customFieldsStatus: "idle",
};

export const customFieldsSlice = createSlice({
    name: "customFields",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch all fields
        builder.addCase(fetchCustomFields.pending, (state, action) => {
            state.customFieldsStatus = "pending";
        });
        builder.addCase(fetchCustomFields.fulfilled, (state, { payload }) => {
            state.customFields = [...(payload ?? [])];
            sessionStorage.setItem("custom_fields", JSON.stringify([...(payload ?? [])]));
            state.customFieldsStatus = "succeeded";
        });
        builder.addCase(fetchCustomFields.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!isAppUnavailablePayload(payload)) {
                toast.error(`${translation}`, {
                    toastId: (payload as any)?.message ?? "ServerError.InternalServerError",
                });
            }
            state.customFieldsStatus = "rejected";
        });

        // Create field
        builder.addCase(createCustomField.fulfilled, (state, { payload }) => {
            if (payload) {
                state.customFields = [...state.customFields, payload];
            }
            toast.success(`${t("CustomFields.Store.CreationSucceeded")}`);
        });
        builder.addCase(createCustomField.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!isAppUnavailablePayload(payload)) {
                toast.error(`${translation}`, {
                    toastId: (payload as any)?.message ?? "ServerError.InternalServerError",
                });
            }
        });

        // Fetch fields of resource
        builder.addCase(fetchCustomFieldsOfResource.fulfilled, (state, { payload }) => {
            state.resourceCustomFields = payload;
            state.resourceCustomFieldsStatus = "succeeded";
        });
        builder.addCase(fetchCustomFieldsOfResource.pending, (state) => {
            state.resourceCustomFieldsStatus = "pending";
        });
        builder.addCase(fetchCustomFieldsOfResource.rejected, (state, { payload }) => {
            state.resourceCustomFieldsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!isAppUnavailablePayload(payload)) {
                toast.error(`${translation}`, {
                    toastId: (payload as any)?.message ?? "ServerError.InternalServerError",
                });
            }
        });

        // Fetch one field
        builder.addCase(fetchCustomField.fulfilled, (state, { payload }) => {
            state.customField = payload;
        });
        builder.addCase(fetchCustomField.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!isAppUnavailablePayload(payload)) {
                toast.error(`${translation}`, {
                    toastId: (payload as any)?.message ?? "ServerError.InternalServerError",
                });
            }
        });

        // Update one field
        builder.addCase(updateCustomField.fulfilled, (state, { payload }) => {
            const customFields = [...state.customFields];
            state.customFields = customFields.map((field) => {
                if (field.id === payload.id) {
                    return payload;
                } else {
                    return field;
                }
            });
            toast.success(`${t("CustomFields.Store.EditSucceeded")}`);
        });
        builder.addCase(updateCustomField.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!isAppUnavailablePayload(payload)) {
                toast.error(`${translation}`, {
                    toastId: (payload as any)?.message ?? "ServerError.InternalServerError",
                });
            }
        });

        // Delete one field
        builder.addCase(deleteCustomField.fulfilled, (state, action) => {
            const customFields = [...state.customFields];
            state.customFields = customFields.filter((field) => field.id !== action.meta.arg.fieldId);
            toast.success(`${t("CustomFields.Store.DeletionSucceeded")}`);
        });
        builder.addCase(deleteCustomField.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!isAppUnavailablePayload(payload)) {
                toast.error(`${translation}`, {
                    toastId: (payload as any)?.message ?? "ServerError.InternalServerError",
                });
            }
        });
    },
});

export default customFieldsSlice.reducer;
